import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Typography,
  TableSortLabel,
  Tooltip,
  Button,
} from '@mui/material';
import { EditOutlined, DeleteOutlined, MailOutlined, DraftsOutlined, InsertDriveFileOutlined, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useSortTable from '../../../hooks/useSortTable';

const CorrespondenceTable = ({ correspondences, onEdit, onDelete, onToggleRead, page, onPageChange, pagination, canEdit, canDelete }) => {
  const navigate = useNavigate();
  const { sorted, sortBy, sortDir, handleSort } = useSortTable(correspondences);
  const [expandedRow, setExpandedRow] = useState(null);

  if (!correspondences || correspondences.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <MailOutlined sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <p>No hay correspondencias registradas</p>
      </Paper>
    );
  }

  const getStatusLabel = (status) => {
    const labels = { registered: 'Registrada', assigned: 'Asignada', closed: 'Cerrada' };
    return labels[status] || status;
  };

  const statusBg = { registered: '#FFFDE7', assigned: '#E3F2FD', closed: '#E8F5E9' };

  const SortCell = ({ label, field, sx: extraSx }) => (
    <TableCell onClick={() => handleSort(field)} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...extraSx }}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const toggleExpand = (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    setExpandedRow((prev) => prev === id ? null : id);
  };

  const totalPages = pagination ? Math.ceil((pagination.total || 0) / (pagination.limit || 10)) : 1;
  const from = pagination ? ((page - 1) * (pagination.limit || 10)) + 1 : 1;
  const to = pagination ? Math.min(page * (pagination.limit || 10), pagination.total || 0) : correspondences.length;

  return (
    <Box>
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortCell label="Rad. Entrada" field="in_settled" />
                <SortCell label="Rad. Salida" field="out_settled" sx={{ display: { xs: 'none', md: 'table-cell' } }} />
                <SortCell label="Titulo" field="title" />
                <SortCell label="Remitente" field="correspondenceUser.name" sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                <SortCell label="Tipo" field="correspondenceType.name" sx={{ display: { xs: 'none', md: 'table-cell' } }} />
                <SortCell label="Estado" field="status" />
                <SortCell label="Asignado a" field="assignedUser.name" sx={{ display: { xs: 'none', lg: 'table-cell' } }} />
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, whiteSpace: 'nowrap' }}>Hilos</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, whiteSpace: 'nowrap' }}>Docs</TableCell>
                <SortCell label="Fecha" field="createdAt" sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((correspondence) => {
                const isExpanded = expandedRow === correspondence.id;
                const docs = correspondence.documents || [];
                const hasMultipleDocs = docs.length > 1;

                return (
                    <TableRow
                      key={correspondence.id}
                      hover
                      onClick={() => navigate(`/correspondences/${correspondence.id}`)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: statusBg[correspondence.status] || 'inherit',
                        ...(!correspondence.isRead && {
                          borderLeft: '3px solid',
                          borderLeftColor: 'primary.main',
                        }),
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: '"Barlow Condensed", system-ui, sans-serif', fontWeight: 600 }}>
                          {correspondence.in_settled}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {correspondence.out_settled ? (
                          <Typography variant="body2">{correspondence.out_settled}</Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">&mdash;</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 240 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 400,
                            ...(isExpanded ? {} : {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }),
                          }}
                        >
                          {correspondence.title}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {correspondence.correspondenceUser ? (
                          <Typography variant="body2">{correspondence.correspondenceUser.name}</Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">&mdash;</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2">{correspondence.correspondenceType?.name || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(correspondence.status)}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: 'rgba(29,31,32,0.16)',
                            color: '#1d1f20',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        {correspondence.assignedUser ? (
                          <Box>
                            <Typography variant="body2">{correspondence.assignedUser.name}</Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(29,31,32,0.55)' }}>{correspondence.assignedUser.email}</Typography>
                          </Box>
                        ) : (
                          <Chip label="Sin asignar" size="small" variant="outlined" sx={{ borderColor: 'rgba(29,31,32,0.16)', fontSize: '0.75rem' }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 28,
                            border: '1px solid rgba(29,31,32,0.16)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.875rem',
                            fontWeight: 400,
                            color: '#1d1f20',
                          }}
                        >
                          {correspondence._count?.threads || 0}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, maxWidth: 220, verticalAlign: 'top', pt: 1.5 }}>
                        {docs.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {(isExpanded ? docs : docs.slice(0, 1)).map((doc) => (
                              <Chip
                                key={Number(doc.id)}
                                icon={<InsertDriveFileOutlined sx={{ fontSize: 14 }} />}
                                label={doc.name || doc.file_original_name || `Doc ${doc.id}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', maxWidth: 190, justifyContent: 'flex-start', borderColor: 'rgba(29,31,32,0.16)', width: 'fit-content' }}
                              />
                            ))}
                            {!isExpanded && docs.length > 1 && (
                              <Chip
                                label={`+${docs.length - 1}`}
                                size="small"
                                sx={{ fontSize: '0.65rem', height: 20, minWidth: 28, bgcolor: '#eef6ff', color: '#2c455d', width: 'fit-content' }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">&mdash;</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontFamily: '"Barlow Condensed", system-ui, sans-serif', fontWeight: 600 }}>
                          {correspondence.createdAt ? new Date(correspondence.createdAt).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '\u2014'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: 'rgba(29,31,32,0.55)', fontFamily: 'monospace' }}>
                          {correspondence.createdAt ? new Date(correspondence.createdAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={isExpanded ? 'Contraer' : 'Expandir'}>
                          <IconButton
                            size="small"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); setExpandedRow((prev) => prev === correspondence.id ? null : correspondence.id); }}
                            sx={{ width: 36, height: 36 }}
                          >
                            {isExpanded ? <KeyboardArrowUp sx={{ fontSize: 18 }} /> : <KeyboardArrowDown sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={correspondence.isRead ? 'Marcar como no leida' : 'Marcar como leida'}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onToggleRead(correspondence.id); }}
                            sx={{ width: 36, height: 36 }}
                          >
                            {correspondence.isRead ? <DraftsOutlined sx={{ fontSize: 18 }} /> : <MailOutlined sx={{ fontSize: 18, color: '#416180' }} />}
                          </IconButton>
                        </Tooltip>
                        {canEdit && (
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); onEdit(correspondence); }}
                              disabled={correspondence.status === 'closed'}
                              sx={{ width: 36, height: 36 }}
                            >
                              <EditOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); onDelete(correspondence.id); }}
                              sx={{ width: 36, height: 36 }}
                            >
                              <DeleteOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Custom pagination */}
      {pagination && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(29,31,32,0.55)' }}>
            Mostrando {from} de {pagination.total || 0} registros &middot; clic en una fila para ver el detalle
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              sx={{ fontWeight: 600, minWidth: 90 }}
            >
              Anterior
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              sx={{ fontWeight: 600, minWidth: 90 }}
            >
              Siguiente
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CorrespondenceTable;
